import { useCallback, useEffect, useState } from 'react';
import {
  AppLog,
  AppLogSearch,
  FirebaseMessage,
  FirebaseUserModel,
  UserSettings,
} from '../../../types';
import {
  startLogSearch,
  storeFirebaseUser,
  UserKeys,
} from '../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../utils/errors';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useStore } from '../state/zustand-store';
import dayjs, { Dayjs } from 'dayjs';
import AppLogSearchGrid from './app-logs-grid';
import {
  DateRange,
  PickersShortcutsItem,
  DateTimeRangePicker,
  SingleInputDateTimeRangeField,
} from '@mui/x-date-pickers-pro';
import SharedAutocomplete from '../../shared/shared-autocomplete';
import { v4 as uuidv4 } from 'uuid';
import RolesAutocomplete from '../../shared/roles-autocomplete';
import useFirebaseMessageHandler from '../../shared/firebase/useFirebaseMessageHandler';
import { pagesConfig } from '../../shared/pages-config';
import { useUserSettings } from '../../utils/use-user-settings';
import SimpleBreadcrumbsPage from '../../shared/simple-breadcrumbs-page';
import SearchButton from '../../shared/buttons/search-button';
import CancelButton from '../../shared/buttons/cancel-button';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

export interface AppLogSearchForm extends Partial<AppLogSearch> {
  dateRange?: DateRange<Dayjs>;
}

export default function AppLogsPage() {
  const { setSnackbar, firebaseUser, setFirebaseUser } = useStore();
  const { viewType } = useUserSettings();
  const [searchedLogs, setSearchedLogs] = useState<AppLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const today = dayjs();
  const queryClient = useQueryClient();

  const { changeLogRoles, administrationRoles, loggedInUser } =
    useUserSettings();

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: 'Last Hour',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(1, 'hour'), today];
      },
    },
    {
      label: 'Last 24 Hours',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(1, 'day'), today];
      },
    },
    {
      label: 'Last 7 Days',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(7, 'day'), today];
      },
    },
    {
      label: 'Last 30 Days',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(30, 'day'), today];
      },
    },
    { label: 'Clear', getValue: () => [null, null] },
  ];

  const { handleSubmit, control, watch, setValue, getValues } =
    useForm<AppLogSearchForm>({
      defaultValues: {
        dateRange: [today.subtract(1, 'day'), today],
        performingUser: '',
        target: '',
        permissionRequired: '',
      },
    });

  const watchPerformingUser = watch('performingUser') || '';
  const watchTarget = watch('target') || '';

  const onSubmit: SubmitHandler<AppLogSearchForm> = (data) => {
    const newSearch: AppLogSearchForm = {
      ...data,
      startedDate: data.dateRange ? data.dateRange[0]?.toDate().getTime() : 0,
      endDate: data.dateRange ? data.dateRange[1]?.toDate().getTime() : 0,
    };
    if (newSearch.performingUser!.length === 0) {
      delete newSearch.performingUser;
    }
    if (newSearch.target!.length === 0) {
      delete newSearch.target;
    }
    if (newSearch.permissionRequired!.length === 0) {
      delete newSearch.permissionRequired;
    }
    delete newSearch.dateRange;
    mutate(newSearch);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<AppLogSearch>) =>
      startLogSearch(data, firebaseUser.firebaseDbPath),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Searching for logs...',
      });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue searching for the logs. Please refresh and try again.',
      });
    },
  });

  // handle loading
  useEffect(() => {
    if (isPending) {
      setSearchedLogs([]);
      setIsLoading(true);
    }
  }, [isPending]);

  async function handleUpdatingFirebaseUser() {
    const oldId = firebaseUser.firebaseDbPath.split('/');
    const newId = `${oldId[0]}/${oldId[1]}/${Date.now()}`;

    const newUser: FirebaseUserModel = {
      ...firebaseUser,
      firebaseDbPath: newId,
      lastKeepAliveMessageSent: Date.now(),
      alive: Date.now(),
    };

    await storeFirebaseUser(newUser);
    setFirebaseUser(newUser);

    queryClient.setQueryData<UserSettings>([UserKeys.UserSettings], (old) => {
      if (!old) return old;

      return {
        ...old,
        firebaseDbPath: newUser.firebaseDbPath,
      };
    });
  }

  const firebaseCallback = useCallback(
    async (message: FirebaseMessage) => {
      const jsonString = message.message;

      if (!isLoading) {
        return;
      }

      if (jsonString === 'finishedWithError') {
        setIsLoading(false);
        setSnackbar({
          open: true,
          severity: 'error',
          message:
            'There was an issue searching for the logs. Please try again.',
        });

        await handleUpdatingFirebaseUser();
        return;
      }

      if (jsonString === 'finished') {
        setIsLoading(false);
        return;
      }

      // Extract the JSON portion between 'appLog:::' and ':::[firebaseId]'
      const matches = jsonString.match(/^appLog:::(.+):::.*$/);
      if (matches && matches[1]) {
        try {
          const parsedLog: AppLog = JSON.parse(matches[1]);

          parsedLog.id = uuidv4();

          parsedLog.action = parsedLog.action.replace(/&#34;/g, '"');
          parsedLog.action = parsedLog.action.replace(/&#39;/g, "'");
          parsedLog.action = parsedLog.action.replace(/&quot;/g, '"');
          parsedLog.action = parsedLog.action.replace(/&apos;/g, "'");

          setSearchedLogs((prev) => [...prev, parsedLog]);
        } catch (error) {
          console.error('Error parsing log message:', error);
        }
      }
    },
    [firebaseUser.firebaseDbPath, isLoading]
  );

  useFirebaseMessageHandler(
    ['appLog'],
    firebaseUser.firebaseDbPath,
    firebaseCallback
  );

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[{ label: pagesConfig.logging.name }]}
      bookmarkKey={pagesConfig.logging.value}
      topContent={
        <Card sx={{ p: 1 }}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <Controller
              name={'performingUser'}
              control={control}
              render={() => (
                <Box sx={{ flexGrow: 1 }}>
                  <SharedAutocomplete
                    sources={['users', 'groups']}
                    label={'Performing User'}
                    autocompleteValue={watchPerformingUser}
                    setAutoCompleteValue={(value) => {
                      setValue('performingUser', value);
                    }}
                  />
                </Box>
              )}
            />
            <Divider orientation="vertical" flexItem />
            <Controller
              name={'target'}
              control={control}
              render={() => (
                <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                  <SharedAutocomplete
                    sources={['users', 'groups', 'calendars']}
                    autocompleteValue={watchTarget}
                    freeSolo
                    label={'Target'}
                    setAutoCompleteValue={(value) => {
                      setValue('target', value);
                    }}
                  />
                </Box>
              )}
            />
            <Divider orientation="vertical" flexItem />
            <Controller
              name={'permissionRequired'}
              control={control}
              render={() => (
                <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                  <RolesAutocomplete
                    setAutoCompleteValue={(value) => {
                      setValue('permissionRequired', value);
                    }}
                  />
                </Box>
              )}
            />
            <Divider orientation="vertical" flexItem />
            <Controller
              name={'dateRange'}
              control={control}
              render={({ field }) => (
                <DateTimeRangePicker
                  sx={{ width: 355 }}
                  label={'DateTime Range'}
                  calendars={2}
                  {...field}
                  slotProps={{
                    shortcuts: {
                      items: shortcutsItems,
                    },
                    actionBar: { actions: [] },
                  }}
                  slots={{ field: SingleInputDateTimeRangeField }}
                />
              )}
            />
            {isLoading ? (
              <CancelButton
                onClick={async () => {
                  setIsLoading(false);
                  await handleUpdatingFirebaseUser();
                }}
              />
            ) : (
              <SearchButton onClick={handleSubmit(onSubmit)} />
            )}
          </Stack>
        </Card>
      }
      heightOfTopContent={66}
    >
      <AppLogSearchGrid
        key={viewType.loggingGridStates?.activeGridState || 'default'}
        loggedInUser={loggedInUser}
        appLogSearch={getValues()}
        changeLogRoles={changeLogRoles}
        administrationRoles={administrationRoles}
        searchedLogs={searchedLogs || []}
        isLoading={isLoading}
      />
    </SimpleBreadcrumbsPage>
  );
}
